/**
 * Traduce y mejora mensajes de error del backend para mostrar al usuario
 */
export function getErrorMessage(error: any): string {
  const statusCode = error?.response?.status;
  
  // Intentar extraer el mensaje del backend
  const backendMessage = 
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.message;

  // Si hay un mensaje del backend, intentar traducirlo
  if (backendMessage && typeof backendMessage === 'string') {
    return translateErrorMessage(backendMessage);
  }

  // Si no hay mensaje pero hay status code, usar mensaje por defecto según el código
  if (statusCode) {
    return getDefaultMessageByStatusCode(statusCode);
  }

  // Mensaje genérico si no hay información específica
  return 'Ha ocurrido un error inesperado. Por favor, intenta nuevamente.';
}

/**
 * Mensajes por defecto según el código de estado HTTP
 * Usado cuando el backend no envía un mensaje específico
 */
function getDefaultMessageByStatusCode(statusCode: number): string {
  switch (statusCode) {
    case 400:
      return 'Los datos enviados no son válidos. Por favor, verifica la información e intenta nuevamente.';
    case 401:
      return 'El correo electrónico o la contraseña son incorrectos. Por favor, verifica tus datos.';
    case 403:
      return 'Tu cuenta está bloqueada o no tienes permisos para acceder. Contacta al administrador.';
    case 404:
      return 'No se encontró el recurso solicitado.';
    case 409:
      return 'Ya existe un registro con estos datos.';
    case 422:
      return 'Los datos enviados no cumplen con los requisitos. Verifica los campos e intenta nuevamente.';
    case 429:
      return 'Has realizado demasiadas solicitudes. Por favor, espera un momento e intenta nuevamente.';
    case 500:
      return 'Ocurrió un error en el servidor. Nuestro equipo ha sido notificado.';
    case 503:
      return 'El servicio no está disponible temporalmente. Por favor, intenta más tarde.';
    default:
      return 'Ha ocurrido un error inesperado. Por favor, intenta nuevamente.';
  }
}

/**
 * Traduce mensajes de error comunes del backend al español
 */
function translateErrorMessage(message: string): string {
  const lowerMessage = message.toLowerCase();

  // Errores de autenticación
  if (lowerMessage.includes('invalid credentials') || 
      lowerMessage.includes('invalid email or password') ||
      lowerMessage.includes('incorrect password') ||
      lowerMessage.includes('wrong password')) {
    return 'El correo electrónico o la contraseña son incorrectos. Por favor, verifica tus datos.';
  }

  if (lowerMessage.includes('user not found') || 
      lowerMessage.includes('email not found')) {
    return 'No encontramos una cuenta con este correo electrónico.';
  }

  if (lowerMessage.includes('account disabled') || 
      lowerMessage.includes('user disabled') ||
      lowerMessage.includes('account blocked') ||
      lowerMessage.includes('user blocked') ||
      lowerMessage.includes('account suspended') ||
      lowerMessage.includes('user suspended') ||
      lowerMessage.includes('inactive') ||
      lowerMessage.includes('is_active')) {
    return 'Tu cuenta está bloqueada o desactivada. Contacta al administrador para más información.';
  }

  if (lowerMessage.includes('forbidden') ||
      lowerMessage.includes('access denied')) {
    return 'No tienes permisos para acceder a este recurso. Contacta al administrador si crees que esto es un error.';
  }

  if (lowerMessage.includes('unauthorized') || 
      lowerMessage.includes('not authorized')) {
    return 'No tienes permisos para realizar esta acción.';
  }

  if (lowerMessage.includes('token expired') || 
      lowerMessage.includes('session expired')) {
    return 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.';
  }

  if (lowerMessage.includes('missing token')) {
    return 'Tu sesión no es válida. Por favor, inicia sesión nuevamente.';
  }

  // Errores de validación
  if (lowerMessage.includes('email already exists') || 
      lowerMessage.includes('email is already registered') ||
      lowerMessage.includes('duplicate email')) {
    return 'Este correo electrónico ya está registrado. ¿Deseas iniciar sesión?';
  }

  if (lowerMessage.includes('solicitud activa') || 
      lowerMessage.includes('active application') ||
      lowerMessage.includes('duplicate application')) {
    return 'Ya tienes una solicitud activa para este animal. Puedes ver su estado en tu área de adoptante.';
  }

  if (lowerMessage.includes('invalid email')) {
    return 'El formato del correo electrónico no es válido.';
  }

  if (lowerMessage.includes('password too short') || 
      lowerMessage.includes('password must be at least')) {
    return 'La contraseña debe tener al menos 8 caracteres.';
  }

  if (lowerMessage.includes('weak password')) {
    return 'La contraseña es muy débil. Debe incluir mayúsculas, minúsculas y números.';
  }

  // Errores de red
  if (lowerMessage.includes('network error') || 
      lowerMessage.includes('failed to fetch') ||
      lowerMessage.includes('econnrefused')) {
    return 'No se pudo conectar con el servidor. Verifica tu conexión a internet.';
  }

  if (lowerMessage.includes('timeout')) {
    return 'La solicitud tardó demasiado tiempo. Por favor, intenta nuevamente.';
  }

  // Errores del servidor
  if (lowerMessage.includes('internal server error') || 
      lowerMessage.includes('500')) {
    return 'Ocurrió un error en el servidor. Nuestro equipo ha sido notificado.';
  }

  if (lowerMessage.includes('service unavailable') || 
      lowerMessage.includes('503')) {
    return 'El servicio no está disponible temporalmente. Por favor, intenta más tarde.';
  }

  // Errores de recursos
  if (lowerMessage.includes('not found') || 
      lowerMessage.includes('404')) {
    return 'No se encontró el recurso solicitado.';
  }

  // Si no coincide con ningún patrón conocido, devolver el mensaje original
  // pero con primera letra mayúscula
  return message.charAt(0).toUpperCase() + message.slice(1);
}

/**
 * Obtiene un título apropiado para el tipo de error
 */
export function getErrorTitle(error: any): string {
  const statusCode = error?.response?.status;

  switch (statusCode) {
    case 400:
      return 'Datos inválidos';
    case 401:
      return 'Error de autenticación';
    case 403:
      return 'Acceso denegado';
    case 404:
      return 'No encontrado';
    case 409:
      return 'Conflicto';
    case 422:
      return 'Datos inválidos';
    case 500:
      return 'Error del servidor';
    case 503:
      return 'Servicio no disponible';
    default:
      return 'Error';
  }
}
